import { fancyTimeFormat } from "ui/lib/helpers";

export type CoachVideoKind = "headliner" | "intro" | "outro";

type RequiredLength = { label: string; min: number; max: number };

/** Required lengths in seconds, from the Coach Directions for videos. */
export const REQUIRED_VIDEO_LENGTH: Record<CoachVideoKind, RequiredLength> = {
  headliner: { label: "Headliner", min: 45, max: 90 },
  intro: { label: "Intro", min: 30, max: 45 },
  outro: { label: "Complete Workout", min: 15, max: 30 },
};

export function requiredLengthRange(kind: CoachVideoKind): string {
  const { min, max } = REQUIRED_VIDEO_LENGTH[kind];
  return `${min}–${max}`;
}

/** Whole seconds, matching what `fancyTimeFormat` shows, so a video that
 * reads 0:45 is never flagged as over a 45-second limit. Returns null for a
 * length we don't know (missing, zero, or the Infinity a WebM recording reports). */
function wholeSeconds(seconds?: number | null): number | null {
  if (typeof seconds !== "number" || !Number.isFinite(seconds) || seconds <= 0)
    return null;
  return Math.floor(seconds);
}

export function isOutsideRequiredLength(
  kind: CoachVideoKind | null | undefined,
  seconds?: number | null
): boolean {
  if (!kind) return false;
  const whole = wholeSeconds(seconds);
  if (whole === null) return false;
  const { min, max } = REQUIRED_VIDEO_LENGTH[kind];
  return whole < min || whole > max;
}

/** "Intro is 1:12. Required length: 30–45 seconds." */
export function requiredLengthMessage(
  kind: CoachVideoKind,
  seconds?: number | null
): string {
  const required = `Required length: ${requiredLengthRange(kind)} seconds.`;
  const whole = wholeSeconds(seconds);
  if (whole === null) return required;
  return `${REQUIRED_VIDEO_LENGTH[kind].label} is ${fancyTimeFormat(whole)}. ${required}`;
}

/** The single rule for which arnold_generated section is the intro or outro.
 * Names are set by the builder ("intro"/"outro"; "arnold inserted section" is
 * the legacy intro), so match them exactly: a mid-workout media section named
 * "Intro to Tempo" is not the intro. */
export function mediaSectionKind(section?: {
  type?: string;
  name?: string;
}): "intro" | "outro" | null {
  if (section?.type !== "arnold_generated") return null;
  const name = (section.name || "").trim().toLowerCase();
  if (name === "intro" || name === "arnold inserted section") return "intro";
  if (name === "outro") return "outro";
  return null;
}

/** The kind of the media section holding `itemId`, if any. */
export function mediaItemKind(
  itemId: string | undefined,
  sectionState?: { groups?: Record<string, any> }
): CoachVideoKind | null {
  if (!itemId || !sectionState?.groups) return null;
  for (const group of Object.values(sectionState.groups)) {
    if (group?.itemIds?.includes?.(itemId)) return mediaSectionKind(group);
  }
  return null;
}
